/**
 * Day 98.5c — pure resolution logic for the Category↔Marketplace mapping bulk
 * import. Maps raw spreadsheet rows (category by name, channel by key) to FK
 * IDs via caller-supplied lookups, classifying each row as READY (both FKs
 * resolve) or UNRESOLVED (with a reason). Within-file duplicate (category,
 * marketplace) pairs collapse last-wins so the downstream upsert never trips a
 * cardinality error. No I/O — the component owns SheetJS parsing + the upsert;
 * this stays pure so it is unit-testable in isolation.
 */

/** A raw row extracted from the uploaded sheet (cells trimmed; rowNumber is the 1-based source row). */
export interface RawMappingRow {
  rowNumber: number;
  category: string;
  channel: string;
  external_id: string;
  external_path: string;
  external_name: string;
}

/** A row whose category + channel both resolved — ready to upsert. */
interface ReadyMappingRow {
  category_id: string;
  marketplace_id: string;
  external_id: string | null;
  external_path: string | null;
  external_name: string | null;
}

type UnresolvedReason = 'category-not-found' | 'unknown-channel';

/** A row that could not be fully resolved, with the reason it was skipped. */
interface UnresolvedMappingRow {
  rowNumber: number;
  category: string;
  channel: string;
  reason: UnresolvedReason;
}

export interface ResolveResult {
  ready: ReadyMappingRow[];
  unresolved: UnresolvedMappingRow[];
}

/** Normalize a name/channel key for case-insensitive, whitespace-tolerant matching. */
export function normalizeKey(s: string): string {
  return s.trim().toLowerCase();
}

/**
 * Resolve raw mapping rows to FK IDs. A row is READY only if its category name
 * AND its channel both resolve via the supplied lookups; otherwise it is
 * UNRESOLVED with the first failing reason (category is checked before channel).
 * external_* are trimmed and empty strings become null. Within-file duplicate
 * (category_id, marketplace_id) pairs collapse last-wins.
 *
 * @param rows           raw rows from the sheet
 * @param categoryLookup normalized category name -> categories.id
 * @param channelLookup  normalized marketplace channel -> marketplaces.id
 */
export function resolveMappingRows(
  rows: RawMappingRow[],
  categoryLookup: Map<string, string>,
  channelLookup: Map<string, string>
): ResolveResult {
  // Keyed by the composite UNIQUE pair so a repeated pair in the same file
  // collapses last-wins (mirrors the DB's UNIQUE(category_id, marketplace_id)).
  const readyByPair = new Map<string, ReadyMappingRow>();
  const unresolved: UnresolvedMappingRow[] = [];

  for (const row of rows) {
    const categoryId = categoryLookup.get(normalizeKey(row.category));
    if (!categoryId) {
      unresolved.push({
        rowNumber: row.rowNumber,
        category: row.category,
        channel: row.channel,
        reason: 'category-not-found',
      });
      continue;
    }
    const marketplaceId = channelLookup.get(normalizeKey(row.channel));
    if (!marketplaceId) {
      unresolved.push({
        rowNumber: row.rowNumber,
        category: row.category,
        channel: row.channel,
        reason: 'unknown-channel',
      });
      continue;
    }
    readyByPair.set(`${categoryId} ${marketplaceId}`, {
      category_id: categoryId,
      marketplace_id: marketplaceId,
      external_id: row.external_id.trim() || null,
      external_path: row.external_path.trim() || null,
      external_name: row.external_name.trim() || null,
    });
  }

  return { ready: [...readyByPair.values()], unresolved };
}
